import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Logo from "../components/Logo";
import { ArrowRight, ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token");
  const [step, setStep] = useState(tokenFromUrl ? "reset" : "request"); // request | sent | reset | done
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState(tokenFromUrl || "");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequest = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatError(data.detail));
      } else {
        // In dev mode, the token is returned directly
        if (data.reset_token) {
          setResetToken(data.reset_token);
        }
        setStep("sent");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(formatError(data.detail));
      } else {
        setStep("done");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#faf8f3" }}>
      <nav className="px-5 md:px-12 py-4 flex items-center justify-between border-b border-black/[0.06]">
        <Logo />
        <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">Browse</Link>
      </nav>

      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          {step === "request" && (
            <>
              <button onClick={() => navigate("/auth")} className="flex items-center gap-1 text-sm text-[#888] hover:text-[#0d0d0d] mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
              <h1 className="text-3xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="forgot-heading">
                Reset your password
              </h1>
              <p className="text-[#888] mb-8 text-sm">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleRequest} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Email</label>
                  <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" className="rounded-xl border-black/[0.1] h-11" required />
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="forgot-error">{error}</p>}
                <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                  className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm">
                  {loading ? "Sending..." : "Send Reset Link"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </>
          )}

          {step === "sent" && (
            <div className="text-center" data-testid="forgot-sent">
              <div className="w-14 h-14 rounded-2xl bg-[#1a6b3c]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-7 h-7 text-[#1a6b3c]" />
              </div>
              <h1 className="text-3xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2">Check your email</h1>
              <p className="text-[#888] mb-6 text-sm">If an account exists with that email, a reset link has been sent.</p>
              {resetToken && (
                <div className="bg-white rounded-xl border border-black/[0.08] p-4 mb-6 text-left">
                  <p className="text-xs font-medium text-[#888] mb-2">Development mode — reset token:</p>
                  <button onClick={() => { setStep("reset"); }} className="text-sm text-[#1a6b3c] font-semibold hover:underline break-all" data-testid="use-reset-token">
                    Click here to reset your password
                  </button>
                </div>
              )}
              <button onClick={() => navigate("/auth")} className="text-sm text-[#1a6b3c] font-semibold hover:underline">
                Back to sign in
              </button>
            </div>
          )}

          {step === "reset" && (
            <>
              <h1 className="text-3xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2" data-testid="reset-heading">
                Set new password
              </h1>
              <p className="text-[#888] mb-8 text-sm">Enter your new password below.</p>
              <form onSubmit={handleReset} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">New Password</label>
                  <div className="relative">
                    <Input data-testid="reset-password-input" type={showPw ? "text" : "password"} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters"
                      className="rounded-xl border-black/[0.1] h-11 pr-10" required minLength={6} />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888]">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="reset-error">{error}</p>}
                <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
                  className="w-full h-12 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold text-sm">
                  {loading ? "Resetting..." : "Reset Password"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="text-center" data-testid="reset-done">
              <div className="w-14 h-14 rounded-2xl bg-[#1a6b3c]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-7 h-7 text-[#1a6b3c]" />
              </div>
              <h1 className="text-3xl font-black text-[#0d0d0d] font-['Fraunces'] mb-2">Password reset!</h1>
              <p className="text-[#888] mb-6 text-sm">Your password has been changed. You can now sign in.</p>
              <Button onClick={() => navigate("/auth")} data-testid="go-signin-btn"
                className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold px-8">
                Sign In <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
